import type { CycleTimeEntry } from "../../../api/work";
import { SURFACES } from "./pipelineColors";
import { median } from "./pipelineHelpers";

interface Props {
  x: number;
  y: number;
  width: number;
  height?: number;
  laneColor: string;
  entries: CycleTimeEntry[];
}

const WARM = "#d9643a";
const MIN_SEG_WIDTH = 26;      // px — wide enough to fit a "0%" label for every state
const MIN_ABBREV_WIDTH = 44;   // px — "CFP 35%"
const MIN_FULL_WIDTH = 90;     // px — "CLEAR FOR PREP 35%"

function abbrev(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    return words.map((w) => w[0]).join("").toUpperCase().slice(0, 4);
  }
  const clean = name.replace(/[^A-Za-z0-9]/g, "");
  if (clean.length <= 4) return clean.toUpperCase();
  return clean.slice(0, 4).toUpperCase();
}

// `median` lives in `pipelineHelpers`, which already has the index guards.
// The predecessor had a second copy here; one definition is enough.

/**
 * Compute segment widths so every state is visible (min width MIN_SEG_WIDTH)
 * while respecting proportional dwell for states that actually accumulated time.
 * Zero-dwell states get MIN_SEG_WIDTH; the remaining budget is split proportionally
 * across non-zero states.
 */
function computeWidths(entries: CycleTimeEntry[], totalWidth: number): number[] {
  const n = entries.length;
  if (n === 0) return [];

  const minTotal = n * MIN_SEG_WIDTH;
  // Not enough room even for every state at minimum — degrade gracefully by
  // giving everyone an equal share.
  if (totalWidth <= minTotal) {
    return entries.map(() => Math.max(1, Math.floor(totalWidth / n)));
  }

  const budget = totalWidth - minTotal;  // to distribute proportionally
  const total = entries.reduce((s, e) => s + e.mean_days, 0);

  const widths: number[] = entries.map((e) => {
    if (total === 0) return MIN_SEG_WIDTH + budget / n;
    const share = e.mean_days / total;
    return MIN_SEG_WIDTH + share * budget;
  });

  // Round and fix rounding drift so they sum exactly to totalWidth.
  const rounded = widths.map((w) => Math.round(w));
  const drift = totalWidth - rounded.reduce((s, w) => s + w, 0);
  if (drift !== 0) {
    // Put the drift on the largest segment so small segments keep their min width.
    const maxIdx = rounded.reduce(
      (best, w, i) => (w > (rounded[best] ?? 0) ? i : best),
      0,
    );
    rounded[maxIdx] = (rounded[maxIdx] ?? 0) + drift;
  }
  return rounded;
}

/**
 * Labeled stacked cycle-time strip.
 *
 * Every state in the lane's workflow gets a visible segment. Segment width
 * reflects dwell time proportionally with a minimum floor so zero-dwell
 * states don't disappear. Labels scale to the segment width:
 *   wide   → full state name + %
 *   medium → short abbreviation + %
 *   narrow → % only
 *   tiny   → nothing (hover tooltip still shows full name + days + %)
 */
export function CycleTimeStrip({ x, y, width, height = 10, laneColor, entries }: Props) {
  if (entries.length === 0) {
    return <rect x={x} y={y} width={width} height={height} fill={SURFACES.track} rx={1} />;
  }

  const widths = computeWidths(entries, width);
  const total = entries.reduce((s, e) => s + e.mean_days, 0);
  const allZero = total === 0;
  const med = median(entries.map((e) => e.mean_days));
  const maxEntry = entries.reduce((a, b) => (a.mean_days > b.mean_days ? a : b));
  const warmThreshold = Math.max(2 * med, 0.5);

  let offsetX = x;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={SURFACES.track} rx={1} />
      {entries.map((e, i) => {
        // `computeWidths` returns one width per entry, so this index is
        // always present; `?? 0` keeps that provable instead of asserted,
        // and a zero-width segment renders as nothing rather than NaN.
        const segW = widths[i] ?? 0;
        const isSlowest = !allZero && e === maxEntry && e.mean_days >= warmThreshold;
        const fill = isSlowest ? WARM : laneColor;
        const relMag = allZero || maxEntry.mean_days === 0
          ? 0
          : e.mean_days / maxEntry.mean_days;
        const opacity = isSlowest ? 0.9 : 0.25 + relMag * 0.55;
        const pct = allZero ? 0 : Math.round((e.mean_days / total) * 100);
        const pctText = allZero ? "—" : `${pct}%`;
        const label =
          segW >= MIN_FULL_WIDTH   ? `${e.state} ${pctText}` :
          segW >= MIN_ABBREV_WIDTH ? `${abbrev(e.state)} ${pctText}` :
          pctText;
        const seg = (
          <g key={`${e.state}-${i}`}>
            <title>{`${e.state} · ${e.mean_days.toFixed(1)}d · ${pctText}`}</title>
            <rect
              x={offsetX} y={y} width={segW} height={height}
              fill={fill} fillOpacity={opacity}
            />
            {label && (
              <text
                x={offsetX + segW / 2} y={y + height / 2 + 2.5}
                fontFamily="Inter, -apple-system, sans-serif"
                fontSize={7} fontWeight={700}
                fill="#0d0f14" textAnchor="middle"
                style={{ pointerEvents: "none" }}
              >
                {label}
              </text>
            )}
          </g>
        );
        offsetX += segW;
        return seg;
      })}
    </g>
  );
}
